import { groq } from 'next-sanity';

// Shared projection used by all blog queries
const blogProjection = `{
  _id,
  title,
  slug,
  excerpt,
  mainImage,
  readTime,
  isLatestBlog,
  publishedAt,
  _createdAt,
  _updatedAt,
  seo{
    title,
    description,
    image
  },
  author->{
    _id,
    name,
    slug,
    image,
    socialMedia[]->{
      _id,
      platform,
      url,
      icon,
      displayOrder,
      isActive
    }
  },
  categories[]->{
    _id,
    title,
    slug,
    description
  },
  body
}`;

// Query to fetch all blogs
export const allBlogsQuery = groq`
  *[_type == "blog"] | order(publishedAt desc, _createdAt desc) ${blogProjection}
`;

// Dynamic paginated + filtered + sorted query.
// $search, $categoryId can be null - guard clauses below handle that.
// $sortField/$sortOrder are injected directly (see buildPaginatedBlogsQuery) since
// GROQ order() can't take a variable field name as a param.
export const buildPaginatedBlogsQuery = (sortBy: 'most-recent' | 'oldest') => {
  const orderClause =
    sortBy === 'oldest'
      ? 'order(publishedAt asc, _createdAt asc)'
      : 'order(publishedAt desc, _createdAt desc)';

  return groq`
    *[
      _type == "blog"
      && isLatestBlog != true
      && ($categoryId == null || $categoryId in categories[]._ref)
      && ($search == null || title match $search || excerpt match $search)
    ] | ${orderClause} [$start...$end] ${blogProjection}
  `;
};

// Matching count query - must mirror the filter conditions above exactly
export const buildTotalBlogsCountQuery = () => groq`
  count(*[
    _type == "blog"
    && isLatestBlog != true
    && ($categoryId == null || $categoryId in categories[]._ref)
    && ($search == null || title match $search || excerpt match $search)
  ])
`;

// Query to fetch latest blogs (marked as isLatestBlog = true)
export const latestBlogsQuery = groq`
  *[_type == "blog" && isLatestBlog == true] | order(publishedAt desc, _createdAt desc) ${blogProjection}
`;

// Query to fetch all categories
export const allCategoriesQuery = groq`
  *[_type == "category"] | order(title asc) {
    _id,
    title,
    slug,
    description
  }
`;

// Query to fetch blogs by category
export const blogsByCategoryQuery = groq`
  *[_type == "blog" && $categoryId in categories[]._ref] | order(publishedAt desc, _createdAt desc) ${blogProjection}
`;

// Query to fetch a single category by slug
export const categoryBySlugQuery = groq`
  *[_type == "category" && slug.current == $slug][0] {
    _id,
    title,
    slug,
    description
  }
`;

// Query to fetch blogs by category slug
export const blogsByCategorySlugQuery = groq`
  *[_type == "blog" && references(*[_type=="category" && slug.current == $slug][0]._id)] | order(publishedAt desc, _createdAt desc) ${blogProjection}
`;

// Alternative query - fetch blogs by category slug (more explicit)
export const blogsByCategorySlugAlternativeQuery = groq`
  *[_type == "blog" && $categoryId in categories[]._ref] | order(publishedAt desc, _createdAt desc) ${blogProjection}
`;

// Query to fetch a single blog by slug
export const blogBySlugQuery = groq`
  *[_type == "blog" && slug.current == $slug][0] {
    _id,
    title,
    slug,
    excerpt,
    mainImage,
    readTime,
    isLatestBlog,
    publishedAt,
    _createdAt,
    _updatedAt,
    seo{
      title,
      description,
      image
    },
    author->{
      _id,
      name,
      slug,
      image,
      bio,
      socialMedia[]->{
        _id,
        platform,
        url,
        icon,
        displayOrder,
        isActive
      }
    },
    categories[]->{
      _id,
      title,
      slug,
      description
    },
    body
  }
`;
